import { useEffect, useCallback } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  action: () => void;
  preventDefault?: boolean;
}

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const matchingShortcut = shortcuts.find(shortcut => {
      const keyMatch = shortcut.key.toLowerCase() === event.key.toLowerCase();
      const ctrlMatch = !!shortcut.ctrlKey === event.ctrlKey;
      const metaMatch = !!shortcut.metaKey === event.metaKey;
      const shiftMatch = !!shortcut.shiftKey === event.shiftKey;
      const altMatch = !!shortcut.altKey === event.altKey;
      
      return keyMatch && ctrlMatch && metaMatch && shiftMatch && altMatch;
    });

    if (matchingShortcut) {
      if (matchingShortcut.preventDefault !== false) {
        event.preventDefault();
      }
      matchingShortcut.action();
    }
  }, [shortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Return formatted shortcuts for help display
  const getShortcutDisplay = useCallback(() => {
    return shortcuts.map(shortcut => {
      const keys = [];
      if (shortcut.ctrlKey) keys.push('Ctrl');
      if (shortcut.metaKey) keys.push('⌘');
      if (shortcut.shiftKey) keys.push('Shift');
      if (shortcut.altKey) keys.push('Alt');
      keys.push(shortcut.key.toUpperCase());
      
      return {
        keys: keys.join(' + '),
        description: shortcut.description
      };
    });
  }, [shortcuts]);

  return { getShortcutDisplay };
};

// Common keyboard shortcuts
export const createCommonShortcuts = (actions: {
  openSearch?: () => void;
  openHelp?: () => void;
  refresh?: () => void;
  toggleSidebar?: () => void;
  newTicket?: () => void;
  save?: () => void;
  export?: () => void;
}): KeyboardShortcut[] => {
  const shortcuts: KeyboardShortcut[] = [];

  if (actions.openSearch) {
    shortcuts.push({
      key: 'k',
      metaKey: true,
      description: 'Open search',
      action: actions.openSearch
    });
  }

  if (actions.openHelp) {
    shortcuts.push({
      key: '?',
      shiftKey: true,
      description: 'Show help',
      action: actions.openHelp
    });
  }

  if (actions.refresh) {
    shortcuts.push({
      key: 'r',
      metaKey: true,
      description: 'Refresh data',
      action: actions.refresh
    });
  }

  if (actions.toggleSidebar) {
    shortcuts.push({
      key: 'b',
      metaKey: true,
      description: 'Toggle sidebar',
      action: actions.toggleSidebar
    });
  }

  if (actions.newTicket) {
    shortcuts.push({
      key: 'n',
      metaKey: true,
      description: 'New ticket',
      action: actions.newTicket
    });
  }

  if (actions.save) {
    shortcuts.push({
      key: 's',
      metaKey: true,
      description: 'Save',
      action: actions.save
    });
  }

  if (actions.export) {
    shortcuts.push({
      key: 'e',
      metaKey: true,
      shiftKey: true,
      description: 'Export data',
      action: actions.export
    });
  }

  return shortcuts;
};